import { useCallback, useEffect } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import {
  VESSEL_REGISTRATION_REMARKS_MAX_CHARS,
  type VesselRegisterFormData,
} from "@features/vessel-register/schema/vesselRegisterFormSchema";
import { FORM_FIELDS, generateAdditionalDocumentField } from "@features/vessel-register/utils";
import type { AdditionalDocumentType } from "@services/vessel/types";
import { t } from "i18next";
import { FormProvider, useFieldArray } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";
import { useUploadAdditionalDocuments } from "./useUploadAdditionalDocument";

const emptyArray: never[] = [];
const LANG_BASE = "vessel-management";
const BUNKER_BARGE_LANG_BASE = "vessel-management.bunker-barge-page";
const fieldName = FORM_FIELDS.ADDITIONAL_DOCUMENTS;
const DOCUMENT_NAME_MAX_CHARS = 50;
const ADDITIONAL_DOCUMENT_TYPES: AdditionalDocumentType[] = [
  "CERTIFICATE",
  "PERMIT",
  "DOCUMENT",
  "INSURANCE",
  "VESSEL_MASTER_SOF",
  "PRE_SAILING_MASTER_DECLARATION",
  "OTHER_SPECIFY",
];
const ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS: Record<AdditionalDocumentType, string> = {
  CERTIFICATE: "certificate",
  PERMIT: "permit",
  DOCUMENT: "document",
  INSURANCE: "insurance",
  VESSEL_MASTER_SOF: "vessel-master-sof",
  PRE_SAILING_MASTER_DECLARATION: "pre-sailing-master-declaration",
  OTHER_SPECIFY: "other-specify",
};

const MAX_FILES = 25;

type AdditionalDocumentUploadDocument = {
  name: string;
  url: string;
  type?: string;
  documentType?: AdditionalDocumentType | "";
  otherSpecifyType?: string;
  documentName?: string;
  documentSummary?: string;
  issueDate?: Date | string | null;
  expiryDate?: Date | string | null;
};

const getAdditionalDocumentKey = (file: UploadEntry, idx: number) => {
  const entry = file as Record<string, unknown>;
  const url = typeof entry.url === "string" && entry.url.trim() ? entry.url : undefined;

  if (url) return url;

  const name = typeof entry.name === "string" && entry.name.trim() ? entry.name : undefined;
  const lastModified = typeof entry.lastModified === "number" ? entry.lastModified : undefined;
  const size = typeof entry.size === "number" ? entry.size : undefined;
  const type = typeof entry.type === "string" && entry.type.trim() ? entry.type : undefined;
  const signature = [name, size, type, lastModified]
    .filter((value) => value !== undefined)
    .join("|");

  return signature || `${fieldName}-${idx}`;
};

const buildDateMinValue = (dateValue: unknown) => {
  if (!dateValue) return undefined;

  const date = new Date(dateValue as string | Date);
  if (Number.isNaN(date.getTime())) return undefined;

  date.setDate(date.getDate() + 1);
  return date;
};

const getDocumentEntriesFromValue = (value: unknown): AdditionalDocumentUploadDocument[] => {
  if (!Array.isArray(value)) return [];

  return value.filter((entry): entry is AdditionalDocumentUploadDocument => {
    if (!entry || entry instanceof File || typeof entry !== "object") return false;

    const candidate = entry as Record<string, unknown>;
    return typeof candidate.name === "string" && typeof candidate.url === "string";
  });
};

const getFormDocumentEntries = (value: unknown): AdditionalDocumentUploadDocument[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry) => {
    if (!entry || entry instanceof File || typeof entry !== "object") return [];

    const candidate = entry as Record<string, any>;
    const name = candidate.name ?? candidate.fileName ?? candidate.documentName;
    const url = candidate.url ?? candidate.documentUrl;

    if (typeof name !== "string" || typeof url !== "string") return [];

    return [{ ...candidate, name, url } as AdditionalDocumentUploadDocument];
  });
};

const mergeDocumentMetadata = (
  documents: AdditionalDocumentUploadDocument[],
  metadataDocuments: AdditionalDocumentUploadDocument[],
) => {
  const maxLength = Math.max(documents.length, metadataDocuments.length);

  return Array.from({ length: maxLength }, (_value, idx) => {
    const document = documents[idx];
    const metadataDocument = metadataDocuments[idx];

    if (!document) return metadataDocument;
    if (!metadataDocument) return document;

    return {
      ...document,
      ...metadataDocument,
      name: document.name || metadataDocument.name,
      url: document.url || metadataDocument.url,
      type: document.type || metadataDocument.type,
    };
  }).filter((document): document is AdditionalDocumentUploadDocument => Boolean(document));
};

const AdditionalDocuments = () => {
  const { formData, updateFormData, isRestrictedToCertificatesUpdate } =
    useOutletContext<VesselOutletContext>();
  const { notify } = useNotification();
  const { additionalDocuments: uploadConditions } = useFileUploadProfiles();

  const additionalDocumentFiles = useUploadStore(
    (state) => state.filesByKey[fieldName] ?? emptyArray,
  );
  const uploadsInFlight = useUploadStore((state) => state.uploadsInFlightByKey[fieldName] ?? 0);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);

  const { remove } = useFieldArray({
    control: formData.control,
    name: FORM_FIELDS.ADDITIONAL_DOCUMENTS,
  });

  const additionalDocuments = formData.watch(FORM_FIELDS.ADDITIONAL_DOCUMENTS) ?? [];
  const additionalDocumentTypeOptions = ADDITIONAL_DOCUMENT_TYPES.map((value) => ({
    label: t(
      `${BUNKER_BARGE_LANG_BASE}.document-types.${ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS[value]}`,
    ),
    value,
  }));

  useEffect(() => {
    const uploadStore = useUploadStore.getState();
    if (uploadStore.getFilesForKey(fieldName).length) return;

    const storedDocuments = getFormDocumentEntries(formData.getValues(fieldName));
    if (!storedDocuments.length) return;

    const normalizedEntries = storedDocuments.map((doc, idx) => {
      const typedDoc = doc as Record<string, any>;
      const fallbackFromUrl =
        typeof typedDoc.url === "string" && typedDoc.url.includes("/")
          ? typedDoc.url.split("/").pop()
          : typedDoc.url;
      const resolvedName =
        typedDoc.name ||
        (typeof typedDoc.documentName === "string" && typedDoc.documentName.trim()) ||
        fallbackFromUrl ||
        `document-${idx + 1}`;

      return {
        name: resolvedName,
        url: typedDoc.url,
        type: typedDoc.documentType,
        documentType: typedDoc.documentType,
        otherSpecifyType: typedDoc.otherSpecifyType ?? "",
        documentName: typedDoc.documentName ?? typedDoc.name ?? resolvedName,
        documentSummary: typedDoc.documentSummary ?? "",
        issueDate: typedDoc.issueDate ?? null,
        expiryDate: typedDoc.expiryDate ?? null,
      };
    });

    uploadStore.setFilesForKey(fieldName, normalizedEntries, {
      mode: "replace",
      maxFiles: MAX_FILES,
    });
  }, [formData]);

  const setDocumentValue = useCallback(
    (documents: AdditionalDocumentUploadDocument[]) => {
      const formDocuments = documents.map((document) => ({
        [FORM_FIELDS.DOCUMENT_URL]: document.url,
        [FORM_FIELDS.FILE_NAME]: document.name,
        [FORM_FIELDS.DOCUMENT_TYPE]: document.documentType ?? "",
        [FORM_FIELDS.OTHER_SPECIFY_TYPE]: document.otherSpecifyType ?? "",
        [FORM_FIELDS.DOCUMENT_NAME]: document.documentName ?? document.name,
        [FORM_FIELDS.DOCUMENT_SUMMARY]: document.documentSummary ?? "",
        [FORM_FIELDS.ISSUE_DATE]: document.issueDate ?? null,
        [FORM_FIELDS.EXPIRY_DATE]: document.expiryDate ?? null,
      }));

      formData.setValue(fieldName, formDocuments, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData(fieldName, formDocuments);
    },
    [formData, updateFormData],
  );

  const getCurrentDocumentsForUpload = useCallback(() => {
    const storeDocuments = getDocumentEntriesFromValue(
      useUploadStore.getState().getFilesForKey(fieldName),
    );
    const formDocuments = getFormDocumentEntries(formData.getValues(fieldName));

    return mergeDocumentMetadata(storeDocuments, formDocuments);
  }, [formData]);

  const handleUploadSuccess = useCallback(
    ({
      documentUrls,
      startIdx,
      files,
    }: {
      documentUrls: string[];
      startIdx: number;
      files: File[];
    }) => {
      const nextDocuments = [
        ...getFormDocumentEntries(formData.getValues(fieldName)),
      ] as AdditionalDocumentUploadDocument[];

      documentUrls.forEach((url, fileOffset) => {
        const at = startIdx + fileOffset;
        const uploadedFile = files[fileOffset];
        const fileName = uploadedFile?.name ?? `document-${at + 1}`;
        const existingDocument = nextDocuments[at];

        nextDocuments[at] = {
          ...existingDocument,
          name: fileName,
          url,
          type: uploadedFile?.type,
          documentType: existingDocument?.documentType ?? "",
          otherSpecifyType: existingDocument?.otherSpecifyType ?? "",
          documentName:
            existingDocument?.documentName ?? fileName.slice(0, DOCUMENT_NAME_MAX_CHARS),
          documentSummary: existingDocument?.documentSummary ?? "",
          issueDate: existingDocument?.issueDate ?? null,
          expiryDate: existingDocument?.expiryDate ?? null,
        };
      });

      const normalizedDocuments = nextDocuments
        .filter((document) => !!document?.url)
        .slice(-MAX_FILES);

      setDocumentValue(normalizedDocuments);
      setFilesForKey(fieldName, normalizedDocuments, { mode: "replace", maxFiles: MAX_FILES });
      formData.clearErrors(fieldName);
    },
    [formData, setDocumentValue, setFilesForKey],
  );

  const handleUploadError = useCallback(
    (error: Error, meta: { startIdx: number; count: number }): void => {
      notify({
        title: t("vessel-management.notifications.additional-documents-upload-error-title"),
        description: t(
          "vessel-management.notifications.additional-documents-upload-error-description",
        ),
        type: "error",
      });
      console.error("Error on upload documents:", error);

      const { startIdx, count } = meta;
      const toRemoveDescending = Array.from({ length: count }, (_, i) => startIdx + i).reverse();

      toRemoveDescending.forEach((idx) => {
        useUploadStore.getState().removeFileAtIndex(fieldName, idx);
      });
    },
    [notify],
  );

  const uploadAdditionalDocuments = useUploadAdditionalDocuments({
    handleUploadSuccess,
    handleUploadError,
  });

  const handleDocumentUpload = (incomingFiles: File[]) => {
    if (!incomingFiles?.length) return;

    const currentDocuments = getCurrentDocumentsForUpload();
    setDocumentValue(currentDocuments);
    uploadAdditionalDocuments.mutate({
      files: incomingFiles,
      startIdx: currentDocuments.length,
    });
  };

  const setDocumentFiles = (files: UploadEntry[]) => {
    const currentDocuments = getCurrentDocumentsForUpload();
    const incomingUploadedDocuments = getDocumentEntriesFromValue(files);
    const incomingFiles = files.filter((file): file is File => file instanceof File);
    const mergedDocuments = mergeDocumentMetadata(incomingUploadedDocuments, currentDocuments);

    setFilesForKey(fieldName, [...mergedDocuments, ...incomingFiles], {
      mode: "replace",
      maxFiles: MAX_FILES,
    });
  };

  const validateUploadFiles = (incomingFiles: File[]) => {
    const existingFilesCount = useUploadStore.getState().getFilesForKey(fieldName).length ?? 0;

    if (existingFilesCount + incomingFiles.length > MAX_FILES) {
      return t("form-validation.max-images", { max: MAX_FILES });
    }

    return null;
  };

  const handleReject = () => {
    const typesLabel = uploadConditions.format.join(", ");
    formData.setError(fieldName, {
      type: "manual",
      message: t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    });
  };

  const handleRemoveFile = (idx: number) => {
    const currentDocuments = [...(formData.getValues(fieldName) ?? [])];
    const nextDocuments = currentDocuments.filter((_v: unknown, i: number) => i !== idx);

    remove(idx);
    formData.setValue(fieldName, nextDocuments, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    updateFormData(fieldName, nextDocuments);
  };

  const renderAdditionalDocumentMetadata = (idx: number) => {
    const document = additionalDocuments[idx] as
      | {
          documentType?: string;
          issueDate?: Date | string | null;
        }
      | undefined;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FormItem<VesselRegisterFormData>
          testId={`additional-documents-${idx}-document-type`}
          name={generateAdditionalDocumentField.documentType(idx)}
          type="select"
          options={additionalDocumentTypeOptions}
          label={t(`${BUNKER_BARGE_LANG_BASE}.document-type`)}
          onFieldChange={(key, value) => {
            updateFormData(key, value);

            if (value !== "CERTIFICATE") {
              formData.clearErrors(generateAdditionalDocumentField.expiryDate(idx));
            }
          }}
          disabled={isRestrictedToCertificatesUpdate}
        />
        {document?.documentType === "OTHER_SPECIFY" ? (
          <FormItem<VesselRegisterFormData>
            testId={`additional-documents-${idx}-other-specify-type`}
            name={generateAdditionalDocumentField.otherSpecifyType(idx)}
            type="text"
            maxChars={30}
            label={t(`${BUNKER_BARGE_LANG_BASE}.other-specify-type`)}
            onFieldChange={updateFormData}
            disabled={isRestrictedToCertificatesUpdate}
          />
        ) : (
          <div className="hidden lg:block" aria-hidden="true" />
        )}
        <div className="lg:col-span-2">
          <FormItem<VesselRegisterFormData>
            testId={`additional-documents-${idx}-document-name`}
            name={generateAdditionalDocumentField.documentName(idx)}
            type="text"
            maxChars={DOCUMENT_NAME_MAX_CHARS}
            label={t("vessel-management.document-name")}
            onFieldChange={updateFormData}
            required
            disabled={isRestrictedToCertificatesUpdate}
          />
        </div>
        <FormItem<VesselRegisterFormData>
          testId={`additional-documents-${idx}-issue-date`}
          name={generateAdditionalDocumentField.issueDate(idx)}
          type="date-picker"
          label={t(`${BUNKER_BARGE_LANG_BASE}.issue-date`)}
          onFieldChange={(key, value) => {
            updateFormData(key, value);
            formData.trigger([
              generateAdditionalDocumentField.issueDate(idx),
              generateAdditionalDocumentField.expiryDate(idx),
            ]);
          }}
          timePicker={false}
          disabled={isRestrictedToCertificatesUpdate}
        />
        <FormItem<VesselRegisterFormData>
          testId={`additional-documents-${idx}-expiry-date`}
          name={generateAdditionalDocumentField.expiryDate(idx)}
          type="date-picker"
          label={t(`${BUNKER_BARGE_LANG_BASE}.expiry-date`)}
          onFieldChange={(key, value) => {
            updateFormData(key, value);
            formData.trigger([
              generateAdditionalDocumentField.issueDate(idx),
              generateAdditionalDocumentField.expiryDate(idx),
            ]);
          }}
          timePicker={false}
          dateConstraint="not-past"
          minDate={buildDateMinValue(document?.issueDate)}
          required={document?.documentType === "CERTIFICATE"}
          disabled={isRestrictedToCertificatesUpdate}
        />
        <div className="lg:col-span-2">
          <FormItem<VesselRegisterFormData>
            testId={`additional-documents-${idx}-document-summary`}
            name={generateAdditionalDocumentField.documentSummary(idx)}
            type="text"
            maxChars={300}
            label={t("vessel-management.document-summary")}
            onFieldChange={updateFormData}
            disabled={isRestrictedToCertificatesUpdate}
          />
        </div>
      </div>
    );
  };

  return (
    <div data-testid="additional-documents-page" className="flex flex-col gap-8 pb-8">
      <FormProvider {...formData}>
        <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.REGISTRATION_REMARKS>
          testId="vessel-registration-remarks"
          name={FORM_FIELDS.REGISTRATION_REMARKS}
          type="textarea"
          label={t(`${LANG_BASE}.remarks-instructions`)}
          maxChars={VESSEL_REGISTRATION_REMARKS_MAX_CHARS}
          minRows={3}
          onFieldChange={updateFormData}
          disabled={isRestrictedToCertificatesUpdate}
          showCharCounter
        />
        <div key={fieldName} className="rounded-sm border border-[#E6EDF2] relative">
          <div className="flex items-center bg-[#E6EDF2] px-8 min-h-12 ">
            <span className="font-bold">{t(`${LANG_BASE}.additional-documents`)}</span>
          </div>
          <div className="flex py-4 px-8 gap-4 flex-col">
            <FormItem<VesselRegisterFormData, typeof FORM_FIELDS.ADDITIONAL_DOCUMENTS>
              name={fieldName}
              type="file"
              uploadConditions={uploadConditions}
              testId={`${fieldName}-upload`}
              files={additionalDocumentFiles}
              setFiles={setDocumentFiles}
              onUpload={(_key, uploadedFiles) => handleDocumentUpload(uploadedFiles)}
              onReject={handleReject}
              disabled={isRestrictedToCertificatesUpdate || uploadsInFlight > 0}
              maxFiles={MAX_FILES}
              notSuccess={uploadsInFlight > 0}
              validateDrop={(selectedCount) => {
                if (selectedCount > MAX_FILES) {
                  return t("form-validation.max-images", { max: MAX_FILES });
                }
                return null;
              }}
              fileValidation={validateUploadFiles}
            />
            <div>
              {additionalDocumentFiles.map((file, idx) => {
                const documentKey = getAdditionalDocumentKey(file, idx);

                return (
                  <div className="flex w-full flex-col gap-4" key={documentKey}>
                    <div key={`${documentKey}-file`}>
                      <SingleFile
                        testId={`${fieldName}-${idx}`}
                        file={file}
                        fieldName={fieldName}
                        currentFileIndex={idx}
                        setFiles={() => handleRemoveFile(idx)}
                        removeFn={() => formData.clearErrors(fieldName)}
                        isLoading={uploadsInFlight > 0 && file instanceof File}
                        disabled={isRestrictedToCertificatesUpdate}
                      />
                    </div>
                    {additionalDocuments[idx]?.documentUrl && renderAdditionalDocumentMetadata(idx)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default AdditionalDocuments;
